import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import {
  BG_2,
  BORDER_2,
  FG_1,
  FG_2,
  FG_3,
  FONT_BODY,
  FONT_TITLE,
  FONT_SIZE_BODY,
  FONT_SIZE_XS,
  FONT_SIZE_XXS,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_EXTRABOLD,
  FONT_WEIGHT_REGULAR,
  FONT_WEIGHT_SEMIBOLD,
  PRIMARY_COLOR,
  RADIUS_FULL,
  SHADOW_INSET_SM,
  SPACE_4,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';

/**
 * BetaEnrichmentFunnel — 8-row horizontal bar chart showing SKU enrichment stages.
 *
 * Props:
 *   stages  Array<{ stage, key, label, description, count, pct_of_total,
 *                   pct_of_gmv, drop_pct, kind, threshold }>
 */
function BetaEnrichmentFunnel({ stages }) {
  // Trigger bar width animation after mount
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 60);
    return () => clearTimeout(timer);
  }, []);

  if (!stages || stages.length === 0) {
    return (
      <div className={css(styles.empty)}>No enrichment data available for this selection.</div>
    );
  }

  // Bars scale relative to the largest stage so the biggest bucket fills the row.
  const maxCount = Math.max(...stages.map(s => s.count || 0), 1);

  return (
    <div className={css(styles.funnel)}>
      {/* Column header row — only the two stat columns are labelled */}
      <div className={css(styles.headerRow)}>
        <div />
        <div />
        <div />
        <div className={css(styles.colStat)}>% of SKUs</div>
        <div className={css(styles.colStat)}>% of GMV</div>
      </div>

      {/* Stage rows */}
      {stages.map((stage, idx) => {
        const widthPct = animated ? (stage.count / maxCount) * 100 : 0;
        const count = stage.count != null ? stage.count.toLocaleString('en-US') : '—';
        const pctTotal = stage.pct_of_total != null ? `${stage.pct_of_total}%` : '—';
        const pctGmv = stage.pct_of_gmv != null ? `${stage.pct_of_gmv}%` : '—';

        return (
          <div
            key={stage.key}
            className={css(styles.stageRow, idx === stages.length - 1 && styles.stageRowLast)}
          >
            {/* Stage number bubble */}
            <div className={css(styles.stageCircle)}>{stage.stage}</div>

            {/* Label + description */}
            <div className={css(styles.stageLabelCol)}>
              <div className={css(styles.stageLabel)}>{stage.label}</div>
              <div className={css(styles.stageDesc)}>{stage.description}</div>
            </div>

            {/* Progress bar — count centered inside, width relative to max */}
            <div className={css(styles.stageBar)}>
              <div className={css(styles.barFill)} style={{ width: `${widthPct}%` }}>
                <span className={css(styles.barCount)}>{count}</span>
              </div>
            </div>

            {/* % of SKUs */}
            <div className={css(styles.colStat, styles.statValueTotal)}>{pctTotal}</div>

            {/* % of GMV */}
            <div className={css(styles.colStat, styles.statValueGmv)}>{pctGmv}</div>
          </div>
        );
      })}
    </div>
  );
}

const GRID_COLUMNS = '36px 240px 1fr 90px 90px';

const styles = StyleSheet.create({
  funnel: {
    display: 'flex',
    flexDirection: 'column',
  },

  empty: {
    padding: `${SPACE_4}px 0`,
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_BODY,
    color: FG_3,
    textAlign: 'center',
  },

  // ─── Header row ────────────────────────────────────────────────────────────
  headerRow: {
    display: 'grid',
    gridTemplateColumns: GRID_COLUMNS,
    gap: 14,
    alignItems: 'center',
    paddingBottom: 8,
    borderBottom: `1px solid ${BORDER_2}`,
  },

  // ─── Stage row ─────────────────────────────────────────────────────────────
  stageRow: {
    display: 'grid',
    gridTemplateColumns: GRID_COLUMNS,
    gap: 14,
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: `1px dashed ${BORDER_2}`,
  },

  stageRowLast: {
    borderBottom: 'none',
  },

  // ─── Stage number circle ──────────────────────────────────────────────────
  stageCircle: {
    width: 28,
    height: 28,
    borderRadius: RADIUS_FULL,
    background: BG_2,
    border: `1px solid ${BORDER_2}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_XS,
    fontWeight: FONT_WEIGHT_BOLD,
    color: FG_2,
    flexShrink: 0,
  },

  // ─── Label column ──────────────────────────────────────────────────────────
  stageLabelCol: {
    minWidth: 0,
  },

  stageLabel: {
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_BODY,
    fontWeight: FONT_WEIGHT_BOLD,
    color: FG_1,
  },

  stageDesc: {
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_XS,
    fontWeight: FONT_WEIGHT_REGULAR,
    color: FG_3,
    marginTop: 2,
    lineHeight: '1.4',
  },

  // ─── Progress bar ──────────────────────────────────────────────────────────
  stageBar: {
    position: 'relative',
    height: 42,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 0,
  },

  barFill: {
    height: '100%',
    minWidth: 60,
    borderRadius: 10,
    backgroundColor: PRIMARY_COLOR,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 14px',
    color: WHITE,
    boxShadow: SHADOW_INSET_SM,
    transition: 'width 0.35s cubic-bezier(0.2, 0.8, 0.2, 1)',
  },

  barCount: {
    fontFamily: FONT_TITLE,
    fontSize: FONT_SIZE_BODY,
    fontWeight: FONT_WEIGHT_EXTRABOLD,
    color: WHITE,
    whiteSpace: 'nowrap',
    letterSpacing: '-0.01em',
    fontVariantNumeric: 'tabular-nums',
  },

  // ─── Stat columns ──────────────────────────────────────────────────────────
  colStat: {
    width: 90,
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_XXS,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    color: FG_3,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    textAlign: 'right',
  },

  statValueTotal: {
    fontFamily: FONT_TITLE,
    fontSize: FONT_SIZE_BODY,
    fontWeight: FONT_WEIGHT_BOLD,
    color: FG_1,
    textTransform: 'none',
    letterSpacing: '-0.01em',
    fontVariantNumeric: 'tabular-nums',
  },

  statValueGmv: {
    fontFamily: FONT_TITLE,
    fontSize: FONT_SIZE_BODY,
    fontWeight: FONT_WEIGHT_BOLD,
    color: FG_2,
    textTransform: 'none',
    letterSpacing: '-0.01em',
    fontVariantNumeric: 'tabular-nums',
  },
});

BetaEnrichmentFunnel.propTypes = {
  stages: PropTypes.arrayOf(
    PropTypes.shape({
      stage: PropTypes.number.isRequired,
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
      count: PropTypes.number,
      pct_of_total: PropTypes.number,
      pct_of_gmv: PropTypes.number,
      drop_pct: PropTypes.number,
      kind: PropTypes.string,
      threshold: PropTypes.string,
    }),
  ).isRequired,
};

export default BetaEnrichmentFunnel;
